import React, { useState, useEffect } from 'react';
import { Check, X, AlertCircle } from 'lucide-react';
import { validatePrimeAmount, validateReimbursementRate, ValidationResult } from '../../utils/validation';

interface EditableAmountProps {
  value: number;
  onSave: (value: number) => boolean;
  onCancel: () => void;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  fieldName?: string;
}

export default function EditableAmount({
  value,
  onSave,
  onCancel,
  suffix = '€',
  min = 0,
  max = 10000,
  step = 1,
  className = '',
  fieldName = 'montant'
}: EditableAmountProps) {
  const [tempValue, setTempValue] = useState(value);
  const [validation, setValidation] = useState<ValidationResult>({ isValid: true, errors: [] });
  const [touched, setTouched] = useState(false);

  // Sync tempValue with prop value when it changes
  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const validateInput = (inputValue: number): ValidationResult => {
    if (suffix === '%') {
      return validateReimbursementRate(inputValue);
    } else {
      return validatePrimeAmount(inputValue, fieldName, max);
    }
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const validationResult = validateInput(tempValue);
    if (!validationResult.isValid) {
      setValidation(validationResult);
      return;
    }

    const success = onSave(tempValue);
    if (!success) {
      setValidation({ isValid: false, errors: ['Impossible de sauvegarder cette valeur'] });
      return;
    }
    
    setValidation({ isValid: true, errors: [] });
    setTouched(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValidation({ isValid: true, errors: [] });
    setTempValue(value);
    setTouched(false);
    onCancel();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setTempValue(newValue);
    setTouched(true);
    
    // Validate on change for immediate feedback
    const validationResult = validateInput(newValue);
    setValidation(validationResult);
  };

  const hasError = !validation.isValid && touched;
  const primaryError = validation.errors[0];

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={tempValue}
            onChange={handleInputChange}
            className={`w-20 px-2 py-1 text-xs border rounded transition-colors ${
              hasError 
                ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }`}
            min={min}
            max={max}
            step={suffix === '€' ? 1 : step}
            onClick={(e) => e.stopPropagation()}
            onFocus={() => setTouched(true)}
            aria-invalid={hasError}
            aria-describedby={hasError ? 'error-message' : undefined}
          />
          <span className="text-xs text-gray-600">{suffix}</span>
          <button
            onClick={handleSave}
            className={`p-1 rounded transition-colors ${
              validation.isValid 
                ? 'text-green-600 hover:bg-green-100' 
                : 'text-gray-400 cursor-not-allowed'
            }`}
            disabled={!validation.isValid}
            title={validation.isValid ? 'Sauvegarder' : 'Corrigez les erreurs avant de sauvegarder'}
          >
            <Check className="w-3 h-3" />
          </button>
          <button
            onClick={handleCancel}
            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
            title="Annuler"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
        
        {/* Error message */}
        {hasError && primaryError && (
          <div id="error-message" className="flex items-center gap-1 mt-1">
            <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-600">{primaryError}</span>
          </div>
        )}
        
        {/* Success feedback */}
        {!touched && validation.isValid && tempValue !== value && (
          <div className="flex items-center gap-1 mt-1">
            <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
            <span className="text-xs text-green-600">Valeur mise à jour</span>
          </div>
        )}
      </div>
    </div>
  );
}